import { QRCodeCanvas } from "qrcode.react";
import { useRef } from "react";
import { Download, MessageCircle } from "lucide-react";
import { waLink, WA_DISPLAY } from "@/lib/contact";

// Print-ready QR codes for business cards, flyers, gurdwara boards, etc.
export default function QRPage() {
  const waRef = useRef(null);
  const siteRef = useRef(null);

  const download = (canvasRef, filename) => {
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.download = filename;
    a.href = url;
    a.click();
  };

  return (
    <div className="min-h-screen bg-forest-900 text-bone-100 px-6 py-16 md:py-24">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight">
          Scan. Message. <span className="italic text-terracotta-400">Done.</span>
        </h1>
        <div className="overline text-terracotta-500 mt-4">Share these QR codes</div>
        <p className="text-bone-100/70 mt-6 max-w-xl leading-relaxed">
          Print on business cards, hand-outs, flyers, or paste in your Instagram / Google Business.
          One tap and the customer is chatting with you or landing on your site.
        </p>

        <div className="grid md:grid-cols-2 gap-8 mt-12">
          {/* WhatsApp QR */}
          <div className="bg-bone-100 p-8 flex flex-col items-center">
            <div ref={waRef}>
              <QRCodeCanvas
                value={waLink()}
                size={280}
                bgColor="#EFEBDF"
                fgColor="#0A1220"
                level="H"
                includeMargin={false}
                imageSettings={{
                  src: "data:image/svg+xml;utf8," + encodeURIComponent(
                    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2325D366'><circle cx='12' cy='12' r='12' fill='white'/><path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z'/></svg>`
                  ),
                  height: 48,
                  width: 48,
                  excavate: true,
                }}
              />
            </div>
            <div className="text-forest-900 mt-6 text-center">
              <div className="overline text-forest-900/60">Scan to chat</div>
              <div className="font-serif text-2xl mt-1">WhatsApp us</div>
              <div className="text-sm text-forest-900/70 mt-1">{WA_DISPLAY}</div>
            </div>
            <div className="flex gap-3 mt-6 w-full">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-4 py-2.5 rounded font-medium text-sm"
              >
                <MessageCircle size={14} fill="white" strokeWidth={0} /> Open chat
              </a>
              <button
                data-testid="download-wa-qr"
                onClick={() => download(waRef, "alwaysinsured-whatsapp-qr.png")}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-forest-900 text-bone-100 px-4 py-2.5 rounded text-sm hover:bg-forest-800"
              >
                <Download size={14} /> Download
              </button>
            </div>
          </div>

          {/* Website QR */}
          <div className="bg-bone-100 p-8 flex flex-col items-center">
            <div ref={siteRef}>
              <QRCodeCanvas
                value="https://alwaysinsured.ca"
                size={280}
                bgColor="#EFEBDF"
                fgColor="#0A1220"
                level="H"
              />
            </div>
            <div className="text-forest-900 mt-6 text-center">
              <div className="overline text-forest-900/60">Scan to visit</div>
              <div className="font-serif text-2xl mt-1">alwaysinsured.ca</div>
              <div className="text-sm text-forest-900/70 mt-1">Get a quote in 60 seconds</div>
            </div>
            <div className="flex gap-3 mt-6 w-full">
              <a
                href="https://alwaysinsured.ca"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-forest-900 text-bone-100 px-4 py-2.5 rounded font-medium text-sm hover:bg-forest-800"
              >
                Open website
              </a>
              <button
                data-testid="download-site-qr"
                onClick={() => download(siteRef, "alwaysinsured-website-qr.png")}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-terracotta-500 text-forest-900 px-4 py-2.5 rounded text-sm font-medium hover:bg-terracotta-400"
              >
                <Download size={14} /> Download
              </button>
            </div>
          </div>
        </div>

        <div className="mt-12 bg-forest-800 border border-bone-100/10 p-6 max-w-2xl">
          <div className="overline text-terracotta-500 mb-3">Sharing tips</div>
          <ul className="space-y-2 text-sm text-bone-100/80 leading-relaxed">
            <li>· Print the WhatsApp QR on the back of your business card</li>
            <li>· Add both QRs to the counter at your office / customer touchpoints</li>
            <li>· Share <code className="text-terracotta-400">alwaysinsured.ca/whatsapp</code> — one clean link opens WhatsApp anywhere</li>
            <li>· Post the website QR on Instagram Stories / community boards</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
